import { SantizationServiceService } from '@/shared/services/santization-service.service';
import { UserAction } from '@/shared/types/core.types';
import { DateUtils } from '@/shared/utils/date-utils';
import { StringUtils } from '@/shared/utils/string-utils';
import { SafeHtml } from '@angular/platform-browser';
import { SurveySimulatorModel } from 'components/simulator';

export class FolderDetail {
    id: number;
    title: string;
    isApprover: boolean;
    objId: number;
    objTitle: string;
}

export enum FilterType {
    STREAMING_TO = 'Streaming To',
    CREATED_BY = 'Created By',
    SEARCH_IN_TYPE = 'Type',
    STATUS = 'Status',
    SKILLS = 'Skills',
    LANGUAGE = 'Language'
}

export type StreamSortType = 'relevance' | 'published' | 'modified';
export type FilterMap = { [index: string]: Array<any> };

export class SearchConditions {
    searchKey: string;
    filters: FilterMap;
    filterType: FilterType;
    sortBy: StreamSortType;

    constructor(object?: any) {
        this.searchKey = object ? object.searchKey : '';
        this.filters = object ? object.filters : { Language: ['EN_GLOBAL'] };
        this.sortBy = object ? object.sortBy : 'relevance';
    }

    filtersCount() {
        const filterKeys = Object.keys(FilterType)
            .map(key => FilterType[key])
            .filter(label => label !== 'Language');

        let filterCount = 0;
        filterKeys.forEach(fKey => {
            if (this.filters[fKey] && this.filters[fKey].length) {
                filterCount = filterCount + 1; // this.filters[fKey].length;
            }
        });
        return filterCount;
    }

    checkpointChanged(checkpoint: FilterMap, includeLanguage = false) {
        const filterKeys = includeLanguage
            ? Object.keys(FilterType).map(key => FilterType[key])
            : Object.keys(FilterType)
                  .map(key => FilterType[key])
                  .filter(label => label !== 'Language');

        let hasChanged = false;
        filterKeys.forEach(fKey => {
            if (this.filters[fKey] && this.filters[fKey].length) {
                const keyFilter = this.filters[fKey];
                const checkpointFilter = checkpoint[fKey] || [];
                if (keyFilter.length !== checkpointFilter.length) {
                    hasChanged = true;
                }
                if (hasChanged === false) {
                    const diffElements = keyFilter.filter(
                        (f, i) => checkpointFilter.indexOf(f) === -1
                    );
                    hasChanged = diffElements.length ? true : false;
                }
            }
        });
        return hasChanged;
    }
}

export class FolderWidgetInfo {
    description: string;
    icon: string;
    id: number;
    isApprover: boolean;
    name: string;
    streamedModules: number;
    totalModule: number;
    unApprovedModules: number;
    baseUrl: string;
}

export class RootStreamInfo {
    rootModuleId: number;
    streamInfo: StreamData;
    existingLangList: Array<Language>;
    streamedDate: string;
    lastUpdatedDate: string;
    langCode: string; // need to remove this once section page is moved to angular
    index: number; // added to sort by relevance
}

/* eslint-disable @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match */
export class StreamData {
    id: number;
    title: string;
    desc: string;
    baseUrl: string;
    can_duplicate: boolean;
    can_edit: boolean;
    can_stream: boolean;
    imageUrl: string;
    isApprover: boolean;
    latestVersion: number;
    maxAssessmentQuestions: number;
    approvedVersionInfo: ApprovedVersionInfo;
    pendingVersionInfo: PendingVersionInfo;
    streamingInfo: StreamingInfo;
    numLanguages: number;
    languageLabel: string;
    // new keys..
    isTranslating: boolean;
    isSynching: boolean;
    isAIGenerating: boolean;
    isAi: boolean;
    synchProgressPercentage: number;
    progressPercentage: number;
    total_sections: number;
    total_cards: number;
    isReference: boolean;
    isCompliance: boolean;
    module_config: ModuleConfig;
    streamConfig: SectionConfig;
    visibility: string;
    numFilesAttached: number;
    categoryId: number;
    // added manually - for duplicate and create stream.
    languageCode?: string;
    existingLangList?: Array<Language>;
    sectionTitle?: string;
    skillIdList?: Array<string>;
    task_id?: string;
    folders?: Array<number>;
    fileName?: string;
    configJson?: string;
}

export class ModuleConfig {
    hasKnowledgeCheck: boolean;
    kcConfig: {
        sendAssess: string;
        numQues: number;
        timerType: string;
        timerValue: string;
    };
    sync_section?: Array<number>;
    ai_creation_in_progress?: boolean;
}

export class SectionConfig {
    alert_on_expiry: boolean;
    assessment: boolean;
    end_date?: string;
    feedback: boolean;
    lock_section: boolean;
    start_date?: string;
    unlock_section: boolean;
    unlock_section_detail: {
        type: string;
        day: string;
        date: string;
    };
    user_acknowlegement: boolean;
}

export class ApprovedVersionInfo {
    assessmentQuestions: number;
    lastUpdatedDate: string;
    modifiedBy: string;
    numCards: number;
    numSections: number;
    status: string;
    version: number;
}

export class PendingVersionInfo {
    assessmentQuestions: number;
    lastUpdatedDate: string;
    modifiedBy: string;
    numCards: number;
    numSections: number;
    status: string;
    version: number;
}

export class StreamingInfo {
    numTeams: number;
    streamConfig?: { StreamConfig };
    streamedBy?: string;
    streamedDate?: string;
    version?: number;
}

export class StreamConfig {
    alert_on_expiry: boolean;
    assessment: boolean;
    end_date: number;
    feedback: boolean;
    lock_section: boolean;
    start_date: number;
    unlock_section: boolean;
    unlock_section_detail: {
        type: string;
        day: string;
        date: string;
    };
    user_acknowlegement: boolean;
}

export class TeamAgencyRoleMap {
    role_id: number;
    agency_id: number;
    business_unit: string;
}

export type AgencyInfo = {
    name: string;
    business_unit: string[];
};
export type AgencyDetails = {
    [key: number]: AgencyInfo;
};

export class TeamCountryData {
    label: string;
    value: string;
    streaming?: boolean;
    details: AgencyDetails;
    business_unit: Array<string>;
    roleCombinations: TeamCombination[];
    assignToAllLanguages: boolean;
    startDate: string;
    endDate: string;
}

export class TeamCombination {
    roleId: number;
    agencyId: number;
    bunit: string;
    agency_id?: number;
    isDuplicate?: boolean;
    id: number;

    static all(): TeamCombination {
        const teamCombination = new TeamCombination();
        teamCombination.roleId = 0;
        teamCombination.agencyId = 0;
        teamCombination.bunit = 'All';
        return teamCombination;
    }
}

export type CommonDataType =
    | 'skills'
    | 'role'
    | 'country'
    | 'skillObjectives'
    | 'language'
    | 'countryAgency';
export type CommonData = Role | Country | Language | Skill | ObjectiveTag;
export type CommonList = {
    [type in CommonDataType]: Array<CommonData>;
};
export class Skill {
    tagId: number;
    tagName: string;
    objectives: ObjectiveTag[];
}

export class ObjectiveTag {
    tagId: number;
    tagName: string;
    hasModule?: boolean;
}

export class Role {
    value: number;
    labelL: string;
}
export class Country {
    label: string;
    value: string;
    business_unit?: Array<string>;
    details?: { [id: number]: CountryAgency };
}

export class CountryAgency {
    name: string;
    business_unit: Array<string>;
}

export class Language {
    value: string;
    label: string;
    translationEnabled: boolean;
}

export class StreamFile {
    id: number;
    name: string;
    description: string;
    downloads: number;
    size: number;
    type: string;
    fileName: string;
}

export class Module {
    module_name: string;
    current_module: ModuleInfo;
    previous_module?: ModuleInfo;
    logo: string;

    static fromJson(json: any): Module {
        const module = new Module();
        module.module_name = json.module_name;
        module.current_module = json.current_module_info;
        module.previous_module = json.previous_module_info;
        module.logo = json.logo;
        return module;
    }
}

export class ModuleInfo {
    sections: ModuleSection[];
    isReference?: boolean;
    version_id: number;
    state: string;
    newly_added_sections?: Array<number>;

    static fromJson(json: any): ModuleInfo {
        const moduleInfo = new ModuleInfo();
        moduleInfo.isReference = json.isReference;
        moduleInfo.version_id = json.version_id;
        moduleInfo.state = json.state;
        moduleInfo.newly_added_sections = json.newly_added_sections ?? [];
        moduleInfo.sections = json.sections.map(section =>
            ModuleSection.fromJson(section, moduleInfo.newly_added_sections)
        );
        return moduleInfo;
    }
}

export class ModuleSection {
    name: string;
    module_id: number;
    duplicated_from: number;
    section_id: number;
    card_count: number;
    objectives: ModuleObjective[];
    is_selected?: boolean;
    isNewlyAdded?: boolean;

    static fromJson(json: any, newlyAddedSections: Array<number>): ModuleSection {
        const moduleSection = new ModuleSection();
        moduleSection.name = json.name;
        moduleSection.module_id = json.module_id;
        moduleSection.duplicated_from = json.duplicated_from;
        moduleSection.section_id = json.section_id;
        moduleSection.card_count = json.card_count;
        moduleSection.objectives = json.objectives.map(objective =>
            ModuleObjective.fromJson(objective)
        );
        moduleSection.isNewlyAdded =
            newlyAddedSections && newlyAddedSections.length > 0
                ? newlyAddedSections.includes(moduleSection.section_id)
                : false;
        return moduleSection;
    }
}

export class ModuleObjective {
    tag_id: number;
    tag_name: string;
    parent_id: number;

    static fromJson(json: any): ModuleObjective {
        const moduleObjective = new ModuleObjective();
        moduleObjective.tag_id = json.tag_id;
        moduleObjective.tag_name = json.tag_name;
        moduleObjective.parent_id = json.parent_id;
        return moduleObjective;
    }
}

export interface Card {
    id: number;
    title: string;
    template_type: string;
    template_id: number;
    entity_id: any;
    duplicated_from: any;
    config: Config;
    duplicated: boolean;
    icon_class: string;
    template_category: string;
    template_name: string;
    translated_from: string;
}

export interface Config {
    [key: string]: any;
}

export interface Question {
    id: number;
    question: string;
    question_type: string;
    parent_question_id: number;
    duplicated_from: any;
    icon_class: string;
    template_id: number;
    translated_from: string;
}

export interface PreviewCard {
    title: string;
    template_type: string;
    ck_html: string;
    safe_html: SafeHtml;
    original_ck_html: any;
    original_title: any;
    template_id: number;
    is_style_applied: boolean;
    styled_text: string;
    cf_url: string;
    template_category: string;
}

export interface ContentHistoryInfo {
    created_by: string;
    from_state: string;
    module_id: number;
    title: string;
    desc: string;
    version: number;
    date: string;
}

export interface ContentSectionCategory {
    tag_id: number;
    tag_name: string;
    objectives: ContentSectionObjectives[];
}

export interface ContentSectionObjectives {
    tag_id: number;
    tag_name: string;
    has_module: boolean;
    parent_id?: number;
    product_name?: string;
}

export interface ContentBreadcrumb {
    name: string;
    isEditable?: boolean;
    description?: string;
    data?: any;
    route?: string;
}

export class ContentGenAIListResponse {
    data: {
        documents: GenAIFile[];
        total_pages: number;
        current_page: number;
        total_documents: number;
    };
    message: string;

    constructor(response: any) {
        this.data = {
            documents: response.data.documents.map((doc: any) => new GenAIFile(doc)),
            total_pages: response.data.total_pages,
            current_page: response.data.current_page,
            total_documents: response.data.total_documents
        };
        this.message = response.message;
    }

    static fromApiResponse(response: any): ContentGenAIListResponse {
        return new ContentGenAIListResponse(response);
    }
}

export class GenAIFile {
    id: number;
    status: 'PENDING' | 'COMPLETE';
    statusDisplay: string;
    file_name: string;
    file_path: string;
    created_at: string;
    updated_at: string;
    displayDate: string;

    constructor(data: any) {
        this.id = data.id;
        this.status = data.status;
        this.file_name = data.file_name;
        this.file_path = data.file_path;
        this.created_at = data.created_at;
        this.updated_at = data.updated_at;
        this.displayDate = DateUtils.fromISO(this.updated_at);
        this.statusDisplay = StringUtils.capitalizeName(this.status);
    }
}

export class SimulatorConfig {
    surveyModel?: SurveySimulatorModel;
    previewCard?: PreviewCard;
    frameWidth?: number;
    frameHeight?: number;
    deviceWidth?: number;
    deviceHeight?: number;

    questionTemplatePreview?: TemplatePreviewResponse;
    preview?: CardFormFieldConfig[];
    urlSummaryPreviewInfo?: UrlResponse;
    viewMode?: 'url' | 'question' | 'content' | 'preview' | 'survey';

    constructor() {}

    static createContentSimulatorObj(
        viewMode?: 'url' | 'question' | 'content' | 'preview' | 'survey'
    ) {
        const simObj = new SimulatorConfig();
        simObj.frameWidth = 250;
        simObj.frameHeight = 532;
        simObj.deviceHeight = 476;
        simObj.deviceWidth = 244;
        simObj.viewMode = viewMode ?? 'question';
        return simObj;
    }
}

export type preview = { key: string; value: any };

export interface CreatePreviewInfo {
    title: string;
    content?: SafeHtml;
    image?: string | ArrayBuffer;
    images?: (string | ArrayBuffer)[];
}

export interface TemplateTypeResponse {
    template_id: number;
    template_type:
        | 'templateImgText01'
        | 'templateImgText02'
        | 'templateImgText03'
        | 'video'
        | 'templateImgText04'
        | 'pdf'
        | 'templateImgText05'
        | 'url';
    icon: string;
    template_category: 'question' | 'content';
    template_preview: TemplatePreviewResponse | null;
    hover_text: string;
    name: string;
}

export interface UrlResponse {
    title: string;
    image: string;
    lsa_content: string;
    lex_content: string;
    first_seven_lines: string;
    url: string;
}

export interface TemplatePreviewResponse {
    type: string;
    title?: string;
    options?: TemplateOption[];
    mediaUrl?: string;
    youtubeUrl?: string;
    mediaFile?: File;
    sequence?: number;
    mediaType?: string;
    explanation?: string;
    correctAnswerOptionIds?: number[];
    typeParams?: TypeParams;
}

export interface TemplateOption {
    title?: string;
    optionId?: number;
    title_type?: string;
    isCorrectAnswer?: boolean;
    mediaUrl?: string;
    mediaFile?: File;
    yAxis?: number;
    xAxis?: number;
}

export interface TypeParams {
    action: string;
    matchOptions: MatchOption[];
    correctValue: any;
    optionPostfixText: any;
    circleMaxValue: any;
    rightRangeDegrees: any;
    progressTemplateConfig: {
        percentage?: any;
        nRemaining?: any;
        number?: any;
        remaining?: any;
        offset?: any;
    };
    draggableText: string;
}

export interface MatchOption {
    title: string;
    mediaUrl: string;
    optionId: number;
    title_type: string;
}

export interface EditCardResponse {
    title: string;
    original_title: string | null;
    original_ck_html: string | null;
    fragments: Fragment[];
    openLinkInBrowser: boolean;
    is_style_applied: boolean;
    cf_url: string;
    styled_text: string;
}

export interface Fragment {
    fragment_id: number;
    fragment_value: string;
}

// export class CardFormFieldConfig {
//     label: string;
//     placeholder?: string;
//     value?: any;
//     required?: boolean;
//     image?: string;
//     type: 'text' | 'image' | 'checkbox' | 'html' | 'side' | 'video';
// }

export type CardFormFieldConfig =
    | TextFormFieldConfig
    | ImageFormFieldConfig
    | CheckboxFormFieldConfig
    | HtmlFormFieldConfig
    | SideFormFieldConfig
    | VideoFormFieldConfig;

interface BaseFormFieldConfig {
    label: string;
    placeholder?: string;
    required?: boolean;
}

interface TextFormFieldConfig extends BaseFormFieldConfig {
    type: 'text';
    value?: string;
}

interface ImageFormFieldConfig extends BaseFormFieldConfig {
    type: 'image';
    value?: File | string;
    image?: string;
}

interface CheckboxFormFieldConfig extends BaseFormFieldConfig {
    type: 'checkbox';
    value?: boolean;
}

export interface HtmlFormFieldConfig extends BaseFormFieldConfig {
    type: 'html';
    value?: string | SafeHtml;
    safeHtml?: SafeHtml;
}

export interface SideFormFieldConfig extends BaseFormFieldConfig {
    type: 'side';
    value?: {
        image: string | ArrayBuffer;
        html: string;
        safeHtml?: SafeHtml;
    };
}

interface VideoFormFieldConfig extends BaseFormFieldConfig {
    type: 'video';
    value: string; // URL for the video
}

export type TemplateType =
    | 'templateImgText01'
    | 'templateImgText02'
    | 'templateImgText03'
    | 'video'
    | 'templateImgText04'
    | 'templateImgText05'
    | 'pdf'
    | 'url'
    | 'import_question'
    | 'content_import'
    | 'mcq_multi'
    | 'mcq_drag'
    | 'mcq';

export class CardFormConfig {
    title?: string;
    fields: CardFormFieldConfig[];
    mode?: UserAction;
    templateType: TemplateType;
    questionResponse?: QuestionEditResponse;
    category: 'question' | 'content';

    private constructor(
        mode: UserAction,
        templateType: TemplateType,
        category: 'question' | 'content',
        private sanitizationService: SantizationServiceService
    ) {
        this.mode = mode;
        this.templateType = templateType;
        this.category = category;
    }

    static createForContent(
        mode: UserAction,
        templateType: TemplateType,
        sanitizationService: SantizationServiceService,
        editCardInfo?: EditCardResponse
    ): CardFormConfig {
        const instance = new CardFormConfig(mode, templateType, 'content', sanitizationService);
        instance.fields = instance.getFieldsForTemplateType(templateType);
        if (editCardInfo) {
            instance.addInfo(editCardInfo);
        }

        return instance;
    }

    static createForQuestion(
        mode: UserAction,
        templateType: TemplateType,
        sanitizationService: SantizationServiceService,
        questionResponse?: QuestionEditResponse
    ): CardFormConfig {
        const instance = new CardFormConfig(mode, templateType, 'question', sanitizationService);

        instance.questionResponse = questionResponse
            ? new QuestionEditResponse(questionResponse)
            : null;
        return instance;
    }

    // constructor(
    //     mode: UserAction,
    //     templateType: TemplateType,
    //     category: 'question' | 'content' = 'content',
    //     private sanitizationService: SantizationServiceService,
    //     editCardInfo?: EditCardResponse
    // ) {
    //     this.mode = mode;
    //     this.templateType = templateType;
    //     this.fields = this.getFieldsForTemplateType(templateType);
    //     this.category = category;
    //     this.addInfo(editCardInfo);
    // }

    createField(
        type: 'text' | 'html' | 'checkbox' | 'image' | 'side' | 'video',
        label: string,
        placeholder?: string
    ): CardFormFieldConfig {
        switch (type) {
            case 'text':
            case 'html':
                return { type, label, placeholder, value: '', safeHtml: '' };
            case 'video':
                return { type, label, placeholder, value: '' };
            case 'checkbox':
                return { type, label, placeholder, value: false };
            case 'image':
                return { type, label, placeholder };
            case 'side':
                return { type, label, value: { image: '', html: '', safeHtml: '' } };
            default:
                return { type, label, placeholder };
        }
    }

    private getFieldsForTemplateType(templateType: string): CardFormFieldConfig[] {
        switch (templateType) {
            case 'templateImgText01':
                return [
                    this.createField('text', 'Title', 'Enter Title'),
                    this.createField('image', 'image1'),
                    this.createField('checkbox', 'checkbox1', 'Add style to numbered list'),
                    this.createField('checkbox', 'checkbox2', 'Open link in browser'),
                    this.createField('html', 'content')
                ];
            case 'templateImgText02':
                return [
                    this.createField('text', 'Title', 'Enter Title'),
                    this.createField('image', 'image1'),
                    this.createField('html', 'content1'),
                    this.createField('image', 'image2'),
                    this.createField('html', 'content2')
                ];

            case 'templateImgText03':
                return [
                    this.createField('text', 'Title', 'Enter Title'),
                    this.createField('checkbox', 'checkbox1', 'Add style to numbered list'),
                    this.createField('checkbox', 'checkbox2', 'Open link in browser'),
                    this.createField('html', 'content1')
                ];

            case 'templateImgText04':
                return [
                    this.createField('text', 'Title', 'Enter Title'),
                    this.createField('checkbox', 'checkbox2', 'Open link in browser'),
                    this.createField('side', 'side1'),
                    this.createField('side', 'side2'),
                    this.createField('side', 'side3'),
                    this.createField('side', 'side4'),
                    this.createField('side', 'side5')
                ];

            case 'video':
                return [
                    this.createField('text', 'Title', 'Enter Title'),
                    this.createField('video', 'video1', 'Youtube video'),
                    this.createField('checkbox', 'checkbox1', 'Add style to numbered list'),
                    this.createField('checkbox', 'checkbox2', 'Open link in browser'),
                    this.createField('html', 'content1')
                ];

            case 'pdf':
                return [this.createField('image', 'image1')];

            default:
                return [];
        }
    }

    private addInfo(editCardInfo: EditCardResponse) {
        if (!editCardInfo) return;
        if (this.templateType === 'templateImgText01') {
            this.fields[0].value = editCardInfo.title;
            if (editCardInfo.fragments[0].fragment_value) {
                this.fields[1].value =
                    editCardInfo.cf_url + editCardInfo.fragments[0].fragment_value;
            }

            this.fields[2].value = editCardInfo.is_style_applied;
            this.fields[3].value = editCardInfo.openLinkInBrowser;
            this.fields[4].value = editCardInfo.fragments[1].fragment_value;
        } else if (this.templateType === 'templateImgText02') {
            this.fields[0].value = editCardInfo.title;
            const fragments = editCardInfo.fragments;
            for (let i = 1; i <= 4; i++) {
                if (i % 2 === 1) {
                    this.fields[i].value = editCardInfo.cf_url + fragments[i - 1].fragment_value;
                } else {
                    this.fields[i].value = fragments[i - 1].fragment_value;
                    (this.fields[i] as HtmlFormFieldConfig).safeHtml =
                        this.sanitizationService.sanitizeHtml(fragments[i - 1].fragment_value);
                }
            }
        } else if (this.templateType === 'templateImgText04') {
            this.fields[0].value = editCardInfo.title;
            for (let i = 0; i < 5; i++) {
                const field = this.fields[i + 2] as SideFormFieldConfig;
                field.value.image =
                    editCardInfo.cf_url + editCardInfo.fragments[i * 2].fragment_value;
                field.value.html = CardFormConfig.cleanHtmlString(
                    editCardInfo.fragments[i * 2 + 1].fragment_value
                );
            }
        } else if (this.templateType === 'video') {
            this.fields[0].value = editCardInfo.title;
            this.fields[1].value = editCardInfo.fragments[0].fragment_value as string;
            this.fields[2].value = editCardInfo.is_style_applied;
            this.fields[3].value = editCardInfo.openLinkInBrowser;
            this.fields[4].value = editCardInfo.fragments[1].fragment_value;
        }
    }

    static cleanHtmlString(htmlString: string): string {
        htmlString = htmlString.replace(/^(<br\s*\/?>|\s)*/g, '');

        const regex = /<p>(<br\s*\/?>|\s*)<\/p>/g;
        const cleanHtml = htmlString.replace(regex, '');

        return cleanHtml.replace(/<p>\s*<\/p>/g, '');
    }
}

export class MatchOptionResponse {
    title: string;
    mediaUrl: string;
    optionId: number;
    titleType: string;

    constructor(data: any) {
        this.title = data.title;
        this.mediaUrl = data.mediaUrl;
        this.optionId = data.optionId;
        this.titleType = data.title_type;
    }

    toJsonMatchQuestion() {
        return {
            title: this.title,
            title_type: this.titleType,
            mediaUrl: this.mediaUrl,
            optionId: this.optionId
        };
    }
}

class TypeParamsResponse {
    action: string;
    matchOptions: MatchOptionResponse[];
    originalText?: string;
    draggableText?: string;

    // Properties for "circular_progress" question type
    correctValue?: string;
    circleMaxValue?: number;
    circleMinValue?: number;
    incrementDegrees?: number;
    optionPostfixText?: string;
    rightRangeDegrees?: string;

    constructor(data: any) {
        this.action = data.action;
        this.matchOptions =
            data?.matchOptions?.map((option: any) => new MatchOptionResponse(option)) ?? [];
        this.originalText = data.originalText;
        this.draggableText = data.draggableText;

        // Initialize properties for "circular_progress" type if available
        this.correctValue = data.correctValue;
        this.circleMaxValue = data.circleMaxValue;
        this.circleMinValue = data.circleMinValue;
        this.incrementDegrees = data.incrementDegrees;
        this.optionPostfixText = data.optionPostfixText;
        this.rightRangeDegrees = data.rightRangeDegrees;
    }

    toJsonMatchQuestion() {
        return {
            action: this.action,
            matchOptions: this.matchOptions.forEach(opt => opt.toJsonMatchQuestion)
        };
    }
}

export class ApiOption {
    title: string;
    optionId: number;
    titleType: string;
    responseType?: string;
    responseValue?: string;
    isCorrectAnswer?: boolean;
    mediaUrl?: File | string;
    matchOptionId?: number;

    // Properties specific to "image_hotspot" question type
    xAxis?: number;
    yAxis?: number;

    constructor(data: any) {
        this.title = data.title;
        this.optionId = data.optionId;
        this.titleType = data.title_type;
        this.responseType = data.response_type;
        this.responseValue = data.response_value;
        this.isCorrectAnswer = data.isCorrectAnswer;
        this.mediaUrl = data.mediaUrl;
        this.matchOptionId = data.matchOptionId;

        // Initialize "image_hotspot" specific properties if available
        this.xAxis = data.xAxis;
        this.yAxis = data.yAxis;
    }

    toJson(): any {
        const json: any = {
            title: this.title,
            optionId: this.optionId,
            title_type: 'TEXT',
            response_type: '',
            response_value: ''
        };
        if (this.isCorrectAnswer !== undefined) {
            json.isCorrectAnswer = this.isCorrectAnswer;
        }
        if (this.mediaUrl !== undefined) {
            json.mediaUrl = '';
        }
        if (this.matchOptionId !== undefined) {
            json.matchOptionId = this.matchOptionId;
        }
        return json;
    }

    toJsonMatchQuestion(): any {
        return {
            title: this.title,
            optionId: this.optionId,
            title_type: this.titleType,
            mediaUrl: this.mediaUrl ?? '',
            matchOptionId: this.matchOptionId
        };
    }

    toJsonOrderQuestion(): any {
        return {
            title: this.title,
            optionId: this.optionId,
            answerSequence: this.optionId,
            title_type: this.titleType
        };
    }

    toMcqDrag(): any {
        const json: any = {
            title: this.title,
            optionId: this.optionId,
            title_type: this.titleType,
            mediaUrl: ''
        };
        if (this.isCorrectAnswer !== undefined) {
            json.isCorrectAnswer = this.isCorrectAnswer;
        }

        return json;
    }
}

export class QuestionEditResponse {
    question: string;
    questionType: string;
    tags: string;
    id: number;
    difficultyLevel: string;
    langCode: string;
    explanation: string;
    mediaType: string;
    mediaUrl: string;
    options: ApiOption[];
    correctAnswerOptionIds: number[];
    staticUrl: string;
    typeParams?: TypeParamsResponse;

    constructor(data: any) {
        this.question = data.question;
        this.questionType = data.question_type;
        this.tags = data.tags;
        this.id = data.id;
        this.difficultyLevel = data.difficulty_level;
        this.langCode = data.langcode;
        this.explanation = data.explanation;
        this.mediaType = data.media_type;
        this.mediaUrl = data.media_url;
        this.options =
            data.options_json.options?.map((option: any) => new ApiOption(option)) ?? null;
        this.correctAnswerOptionIds = data.options_json.correctAnswerOptionIds;
        this.staticUrl = data.staticUrl;
        this.typeParams = data.options_json.typeParams
            ? new TypeParamsResponse(data.options_json.typeParams)
            : undefined;
    }
}

export interface QuestionCardBaseComponent {
    createFormData(): FormData | void;
}

export class SearchCardQuestionResponseObj {
    constructor(
        public id: number,
        public title: string,
        public subtitle: string,
        public number: number,
        public selected?: boolean
    ) {}

    static searchCardQuestionObj(json: any) {
        const obj = Object.values(json).map(
            ([id, title, subtitle, number]) =>
                new SearchCardQuestionResponseObj(id, title, subtitle, number)
        );
        return obj;
    }
}

export class PdfItem {
    title: string;
    imageUrl: string;

    constructor(title: string, imageUrl: string) {
        this.title = title;
        this.imageUrl = imageUrl;
    }
}

export class PdfProcessResponseObject {
    pdfPreviews: PdfItem[];
    orientation: string;
    pdfFile: string;

    constructor(json: any) {
        this.pdfPreviews = json['server_response'].map(item => new PdfItem(item[0], item[1]));
        this.orientation = json['orientation'];
        this.pdfFile = json['pdf_file'];
    }
}
